import { useEffect, useState } from "react";
import { X, Loader2, Bug, Lightbulb } from "lucide-react";
import { toast } from "sonner";
import api from "@/services/api";

/**
 * Feedback modal — opened from "Signaler un bug" / "Donner mon avis".
 * Posts to /api/feedback. Stored in the `feedback` Mongo collection and
 * surfaced in the admin "Retours Communaute" table.
 */
export default function FeedbackModal({ open, type = "bug", onClose }) {
  const [content, setContent] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => { if (open) setContent(""); }, [open]);
  if (!open) return null;

  const isBug = type === "bug";
  const Icon = isBug ? Bug : Lightbulb;
  const title = isBug ? "Signaler un bug" : "Donner mon avis / une idee";
  const placeholder = isBug
    ? "Decris le bug avec un maximum de details (ecran, action, comportement attendu...)"
    : "Quelle fonctionnalite te ferait aimer encore plus MatchUp ?";

  const handleSend = async () => {
    const v = content.trim();
    if (!v) { toast.error("Message vide"); return; }
    setSending(true);
    try {
      await api.submitFeedback(type, v);
      toast.success("Merci pour ton retour !");
      onClose();
    } catch (err) {
      toast.error(err.message || "Erreur");
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      data-testid="feedback-modal"
      style={{
        position: 'fixed', inset: 0, zIndex: 11500,
        background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div style={{
        width: '100%', maxWidth: 380,
        background: '#18181b', border: '1px solid #27272a', borderRadius: 18,
        padding: '20px 18px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <h2 className="font-heading italic uppercase" style={{
            fontSize: 16, fontWeight: 900, color: '#fff', letterSpacing: '0.04em',
            display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <Icon size={16} color="#22c55e" />
            {title}
          </h2>
          <button data-testid="feedback-close" onClick={onClose} aria-label="Fermer" style={{
            width: 32, height: 32, borderRadius: 999, background: '#27272a', color: '#fff',
            border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
          }}><X size={16} /></button>
        </div>
        <textarea
          data-testid="feedback-textarea"
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder={placeholder}
          rows={5}
          maxLength={2000}
          style={{
            width: '100%', padding: 12, fontSize: 13, color: '#fff',
            background: '#0a0a0a', border: '1px solid #27272a', borderRadius: 10,
            resize: 'vertical', outline: 'none', minHeight: 120, lineHeight: 1.45,
          }}
        />
        <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4, textAlign: 'right' }}>
          {content.length}/2000
        </div>
        <button
          data-testid="feedback-send"
          onClick={handleSend}
          disabled={sending}
          style={{
            marginTop: 12, width: '100%', height: 48, borderRadius: 14,
            background: 'linear-gradient(180deg,#22c55e 0%,#16a34a 100%)',
            color: '#fff', fontWeight: 800, fontSize: 14,
            border: 'none', cursor: sending ? 'wait' : 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            boxShadow: '0 6px 16px rgba(34,197,94,0.35)',
            opacity: sending ? 0.7 : 1,
          }}
        >
          {sending && <Loader2 size={16} className="animate-spin" />}
          Envoyer
        </button>
      </div>
    </div>
  );
}
